import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Clock,
    Users,
    Calendar,
    Building2,
    AlertCircle,
    ChevronDown,
    ChevronUp,
    Banknote,
    HandCoins,
    Wallet,
} from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import StatsCards from '../../components/Inbox/StatsCards';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import VerificationInput from '../../components/Inbox/VerificationInput';

import {
    fetchAdvanceInbox,
    fetchAdvanceDetail,
    submitApproveLabourAdvance,
    clearAdvanceDetail,
} from '../../slices/labourAdvanceSlice/labourAdvanceSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

import {
    fetchStatusList,
    selectEnabledActions,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import { buildApproveAdvancePayload, isOwnLabour } from './labourAdvanceHelpers';

// Verify / Approve Bulk Labour Advance (MOID 702), opened from the inbox (/HR/VerifyLabourAdvance,
// /HR/ApproveLabourAdvance). Copied from VerifyLabourPayRoll. spApproveLabourAdvance has no Return.

const MOID = 702;
const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const fmt = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const period = (m, y) => `${MONTH_NAMES[(m || 1) - 1]} ${y || ''}`.trim();

// ─── Main component ────────────────────────────────────────────────────────────
const VerifyLabourAdvance = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();

    // ── Selectors ──
    const inbox          = useSelector((s) => s.labourAdvance.inbox);
    const inboxLoading   = useSelector((s) => s.labourAdvance.inboxLoading);
    const inboxError     = useSelector((s) => s.labourAdvance.inboxError);
    const detail         = useSelector((s) => s.labourAdvance.detail);
    const detailLoading  = useSelector((s) => s.labourAdvance.detailLoading);
    const detailError    = useSelector((s) => s.labourAdvance.detailError);
    const approveLoading = useSelector((s) => s.labourAdvance.approveLoading);
    const remarks        = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);
    const enabledActions = useSelector(selectEnabledActions);
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    // ── Local state ──
    const [selectedItem,         setSelectedItem]         = useState(null);
    const [isVerified,           setIsVerified]           = useState(false);
    const [verificationComment,  setVerificationComment]  = useState('');
    const [showRemarksHistory,   setShowRemarksHistory]   = useState(false);
    const [searchQuery,          setSearchQuery]          = useState('');
    const [filterCC,             setFilterCC]             = useState('All');
    const [filterMonth,          setFilterMonth]          = useState('All');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered,   setIsLeftPanelHovered]  = useState(false);
    const [expandedWorkers,      setExpandedWorkers]      = useState(true);

    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    // ── Init ──
    useEffect(() => {
        dispatch(setShowReturnButton('No'));
        if (roleId) dispatch(fetchAdvanceInbox(roleId));
        return () => {
            dispatch(clearAdvanceDetail());
            dispatch(resetApprovalData());
        };
    }, [roleId, dispatch]);

    // ── Fetch detail when item selected ──
    useEffect(() => {
        if (!selectedItem?.AdvanceId) return;
        dispatch(fetchAdvanceDetail(selectedItem.AdvanceId));
        setIsVerified(false);
        setVerificationComment('');
        setShowRemarksHistory(false);
    }, [selectedItem, dispatch]);

    // ── Fetch status & remarks when detail loads ──
    useEffect(() => {
        if (!selectedItem || !roleId || !detail?.Header) return;
        dispatch(fetchStatusList({ MOID, ROID: roleId, ChkAmt: 0 }));
        dispatch(setSelectedMOID(MOID));
        // spApproveLabourAdvance writes Remarks.TransactionNo = NotifRefno
        dispatch(fetchRemarks({ trno: detail.Header.NotifRefno || selectedItem.NotifRefno || '', moid: MOID }));
    }, [selectedItem, roleId, detail, dispatch]);

    useEffect(() => {
        if (selectedItem) setIsLeftPanelCollapsed(true);
    }, [selectedItem]);

    // ── Filters ──
    const costCentres = useMemo(() => ['All', ...new Set(inbox.map(i => i.CCName).filter(Boolean))], [inbox]);
    const months      = useMemo(() => ['All', ...new Set(inbox.map(i => period(i.AdvanceMonth, i.AdvanceYear)))], [inbox]);

    const filteredInbox = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return inbox.filter(item => {
            const matchCC    = filterCC    === 'All' || item.CCName === filterCC;
            const matchMonth = filterMonth === 'All' || period(item.AdvanceMonth, item.AdvanceYear) === filterMonth;
            const matchQ     = !q
                || (item.CCName || '').toLowerCase().includes(q)
                || (item.CCCode || '').toLowerCase().includes(q)
                || (item.TransactionRefNo || '').toLowerCase().includes(q);
            return matchCC && matchMonth && matchQ;
        });
    }, [inbox, filterCC, filterMonth, searchQuery]);

    // ── Stats cards ──
    const statsCards = useMemo(() => [
        { icon: Clock,    value: inbox.length, label: 'Pending', color: 'indigo' },
        { icon: Users,    value: inbox.reduce((s, i) => s + (i.WorkerCount || 0), 0), label: 'Total Labour', color: 'blue' },
        { icon: Banknote, value: `₹${(inbox.reduce((s, i) => s + (i.TotalAdvance || 0), 0) / 100000).toFixed(2)}L`, label: 'Total Advance', color: 'purple' },
    ], [inbox]);

    // ── Handlers ──
    const handleItemSelect = (item) => {
        dispatch(clearAdvanceDetail());
        setSelectedItem(item);
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchAdvanceInbox(roleId));
        if (selectedItem?.AdvanceId) dispatch(fetchAdvanceDetail(selectedItem.AdvanceId));
    };

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleActionClick = async (action) => {
        if (!selectedItem) { toast.error('No advance selected'); return; }
        if (!verificationComment?.trim()) { toast.error('Verification comment is mandatory before proceeding.'); return; }
        if (!isVerified) { toast.error('Please check the verification checkbox to confirm you have reviewed the advance.'); return; }

        const payload = buildApproveAdvancePayload({
            advanceId: detail?.Header?.AdvanceId || selectedItem.AdvanceId,
            action, note: verificationComment, roleId, createdBy: currentUser,
        });

        try {
            const result = await dispatch(submitApproveLabourAdvance(payload)).unwrap();
            toast.success(result?.Message || `${payload.Action} completed successfully!`);
            setTimeout(() => {
                dispatch(fetchAdvanceInbox(roleId));
                setSelectedItem(null);
                setVerificationComment('');
                setIsVerified(false);
                setShowRemarksHistory(false);
                setIsLeftPanelCollapsed(false);
                dispatch(clearAdvanceDetail());
                dispatch(resetApprovalData());
            }, 800);
        } catch (err) {
            const errMsg = typeof err === 'string' ? err : (err?.message || err?.Message || `Failed to ${payload.Action.toLowerCase()}`);
            toast.error(errMsg, { autoClose: 8000 });
        }
    };

    // ── Data shortcuts ──
    const hdr  = detail?.Header || null;
    const rows = detail?.Details || [];

    // ── Left panel card renderers ──
    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-3">
                <div className="relative">
                    <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-800/50 dark:to-purple-800/50 flex items-center justify-center">
                        <HandCoins className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-purple-500 rounded-full border-2 border-white dark:border-gray-800" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.CCName || item.CCCode || 'Cost Centre'}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.TransactionRefNo}</p>
                </div>
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex items-center justify-between">
                    <span className="flex items-center space-x-1">
                        <Calendar className="w-3 h-3" />
                        <span>{period(item.AdvanceMonth, item.AdvanceYear)}</span>
                    </span>
                    <span className="px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium">
                        Advance
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <span className="flex items-center space-x-1">
                        <Users className="w-3 h-3" />
                        <span>{item.WorkerCount} labour</span>
                    </span>
                    <span className="font-semibold text-gray-800 dark:text-gray-200">₹{fmt(item.TotalAdvance)}</span>
                </div>
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[160px] truncate">
                {item.CCName || item.CCCode || 'Cost Centre'}
            </span>
            <span className="text-gray-500 dark:text-gray-400 min-w-[110px] truncate">{item.TransactionRefNo}</span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[120px]">
                <Calendar className="w-3 h-3" />
                {period(item.AdvanceMonth, item.AdvanceYear)}
            </span>
            <span className="flex items-center gap-1 text-gray-500 dark:text-gray-400 min-w-[90px]">
                <Users className="w-3 h-3" />
                {item.WorkerCount} labour
            </span>
            <span className="font-semibold text-gray-800 dark:text-gray-200 min-w-[100px]">₹{fmt(item.TotalAdvance)}</span>
            <span className="ml-auto px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-full text-xs font-medium whitespace-nowrap">
                Advance
            </span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-800/50 dark:to-purple-800/50 flex items-center justify-center">
            <HandCoins className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    // ── Detail content ──
    const renderDetailContent = () => {
        if (!selectedItem) return null;

        return (
            <div className="space-y-6">
                {detailLoading && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 border border-blue-200 dark:border-blue-700 flex items-center space-x-3">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                        <span className="text-blue-700 dark:text-blue-400 text-sm">Loading advance details…</span>
                    </div>
                )}

                {detailError && (
                    <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-700 flex items-center gap-2">
                        <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-600 dark:text-red-400">{detailError}</p>
                    </div>
                )}

                {hdr && (
                    <>
                        {/* ── Header card ── */}
                        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                <div className="flex items-start space-x-4">
                                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg flex-shrink-0">
                                        <Building2 className="w-7 h-7 text-white" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                            {hdr.CCName || selectedItem.CCName} <span className="text-sm font-normal text-gray-500">({hdr.CCCode})</span>
                                        </h2>
                                        <p className="text-sm text-indigo-600 dark:text-indigo-400 font-semibold mt-0.5">{hdr.TransactionRefNo}</p>
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                                                {period(hdr.AdvanceMonth, hdr.AdvanceYear)}
                                            </span>
                                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                                {hdr.WorkerCount} Labour
                                            </span>
                                            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                                Advance Date {hdr.AdvanceDate}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Advance</p>
                                    <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">₹{fmt(hdr.TotalAdvance)}</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Generated by: <strong>{hdr.CreatedBy}</strong></p>
                                    {hdr.CreatedOn && <p className="text-xs text-gray-400 mt-0.5">{hdr.CreatedOn}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5 pt-5 border-t border-indigo-200 dark:border-indigo-700">
                                {[
                                    { label: 'Own Labour',        value: `₹${fmt(hdr.OwnLabourAdvance)}`,  icon: <Users size={14} />,     color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-900/20' },
                                    { label: 'Contractor Labour', value: `₹${fmt(hdr.ContractorAdvance)}`, icon: <Users size={14} />,     color: 'text-teal-600 dark:text-teal-400',     bg: 'bg-teal-50 dark:bg-teal-900/20' },
                                    { label: 'Wages DCA',         value: hdr.DCACode || '—',               icon: <Wallet size={14} />,    color: 'text-blue-600 dark:text-blue-400',     bg: 'bg-blue-50 dark:bg-blue-900/20' },
                                    { label: 'Budget FY',         value: hdr.FYYear || '—',                icon: <Calendar size={14} />,  color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-900/20' },
                                ].map(({ label, value, icon, color, bg }) => (
                                    <div key={label} className={`${bg} rounded-xl p-3`}>
                                        <div className={`flex items-center gap-1 text-xs mb-1 ${color}`}>{icon}<span>{label}</span></div>
                                        <p className="font-bold text-gray-800 dark:text-gray-100 text-sm">{value}</p>
                                    </div>
                                ))}
                            </div>
                            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                                The total was debited from the wages DCA budget at generation; rejecting gives it back.
                                Approved advance is paid through Labour CMS and recovered in the next payroll.
                            </p>
                        </div>

                        {/* ── Labour-wise ── */}
                        {rows.length > 0 && (
                            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => setExpandedWorkers(p => !p)}
                                    className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        <div className="p-1.5 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg">
                                            <Users size={13} className="text-white" />
                                        </div>
                                        Labour-wise Advance ({rows.length})
                                    </span>
                                    {expandedWorkers ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                                {expandedWorkers && (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-gradient-to-r from-purple-600 to-indigo-700">
                                                <tr>
                                                    {['#', 'Labour ID', 'Name', 'Category', 'Days', 'Daily Rate', 'Earned', 'Unsettled Before', 'Available', 'Advance'].map(h => (
                                                        <th key={h} className="px-3 py-2.5 text-left font-semibold text-white whitespace-nowrap">{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                                {(() => {
                                                    const out = [];
                                                    let lastGroupKey = null;
                                                    let rowNum = 0;
                                                    rows.forEach((w, i) => {
                                                        const own = isOwnLabour(w);
                                                        const groupKey = own ? '__own__' : `__ctr__${w.ContractorName || w.ContractorCode || ''}`;
                                                        if (groupKey !== lastGroupKey) {
                                                            lastGroupKey = groupKey;
                                                            out.push(
                                                                <tr key={`grp-${groupKey}-${i}`} className="bg-purple-50/80 dark:bg-purple-900/20">
                                                                    <td colSpan={10} className="px-3 py-1.5 border-t-2 border-purple-200 dark:border-purple-700">
                                                                        <span className="text-xs font-bold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
                                                                            {own ? 'Own Labour' : `Contractor — ${w.ContractorName || w.ContractorCode || 'Unknown'}`}
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        }
                                                        rowNum++;
                                                        out.push(
                                                            <tr key={w.DetailId || w.LabourId || i} className="hover:bg-purple-50/50 dark:hover:bg-purple-900/10 transition-colors">
                                                                <td className="px-3 py-2 text-gray-400">{rowNum}</td>
                                                                <td className="px-3 py-2 font-mono text-indigo-700 dark:text-indigo-300">{w.LabourId}</td>
                                                                <td className="px-3 py-2 text-gray-800 dark:text-gray-200 whitespace-nowrap font-medium">{w.LabourName}</td>
                                                                <td className="px-3 py-2">
                                                                    <span className="px-1.5 py-0.5 rounded text-xs bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300">{w.Category}</span>
                                                                </td>
                                                                <td className="px-3 py-2 text-center text-blue-700 dark:text-blue-300 font-semibold">{w.DaysWorked}</td>
                                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{fmt(w.DailyRate)}</td>
                                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{fmt(w.EarnedAmount)}</td>
                                                                <td className="px-3 py-2 text-right text-rose-600 dark:text-rose-400">{fmt(w.UnsettledBefore)}</td>
                                                                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">{fmt(w.AvailableAmount)}</td>
                                                                <td className="px-3 py-2 text-right font-bold text-green-700 dark:text-green-400">{fmt(w.AdvanceAmount)}</td>
                                                            </tr>
                                                        );
                                                    });
                                                    return out;
                                                })()}
                                            </tbody>
                                            <tfoot className="bg-indigo-50 dark:bg-indigo-900/20 border-t-2 border-indigo-200 dark:border-indigo-700">
                                                <tr>
                                                    <td colSpan={9} className="px-3 py-2.5 font-bold text-indigo-700 dark:text-indigo-300">Total</td>
                                                    <td className="px-3 py-2.5 text-right font-bold text-green-700 dark:text-green-400">{fmt(hdr.TotalAdvance)}</td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* ── Remarks History ── */}
                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(p => !p)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                {/* ── Verification Input ── */}
                <VerificationInput
                    isVerified={isVerified}
                    onVerifiedChange={setIsVerified}
                    comment={verificationComment}
                    onCommentChange={(e) => setVerificationComment(e.target.value)}
                    config={{
                        checkboxLabel: '✓ I have reviewed the advance and confirm its accuracy',
                        checkboxDescription: 'Including the labour list, days present and advance amounts',
                        commentLabel: 'Verification Comments',
                        commentPlaceholder: 'Enter your verification comments before proceeding...',
                        commentRequired: true,
                        commentRows: 4,
                        commentMaxLength: 1000,
                        showCharCount: true,
                        validationStyle: 'dynamic',
                        checkboxGradient: 'from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20',
                        commentGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        commentBorder: 'border-indigo-200 dark:border-indigo-700',
                    }}
                />

                {/* ── Action Buttons ── */}
                <ActionButtons
                    actions={enabledActions}
                    onActionClick={handleActionClick}
                    loading={approveLoading}
                    isVerified={isVerified}
                    comment={verificationComment}
                    showValidation={true}
                    excludeActions={['return']}
                />
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* ── Header ── */}
            <InboxHeader
                title={`${InboxTitle || 'Labour Advance Verification'} (${inbox.length})`}
                subtitle={ModuleDisplayName || 'Review and approve bulk labour advances'}
                itemCount={inbox.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={HandCoins}
                badgeText="Labour Advance"
                enableViewToggle
                badgeCount={inbox.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by cost centre, ref no...',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                filters={[
                    {
                        value: filterCC,
                        onChange: (e) => setFilterCC(e.target.value),
                        defaultLabel: 'All Cost Centres',
                        options: costCentres.filter(c => c !== 'All'),
                    },
                    {
                        value: filterMonth,
                        onChange: (e) => setFilterMonth(e.target.value),
                        defaultLabel: 'All Months',
                        options: months.filter(m => m !== 'All'),
                    },
                ]}
            />

            {/* ── Stats ── */}
            <div className="px-6 mb-6">
                <StatsCards
                    cards={statsCards}
                    variant="simple"
                    gridCols="grid-cols-1 md:grid-cols-3"
                    gap="gap-4"
                />
            </div>

            {/* ── Body ── */}
            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredInbox,
                    selectedItem: selectedItem,
                    onItemSelect: handleItemSelect,
                    renderItem: renderItemCard,
                    renderListItem: renderListItem,
                    renderCollapsedItem: renderCollapsedItem,
                    loading: inboxLoading,
                    error: inboxError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending Verification',
                        icon: Clock,
                        emptyMessage: 'No pending labour advances for verification',
                        itemKey: 'AdvanceId',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Labour Advance Verification',
                        icon: HandCoins,
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem: selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Advance Details',
                        icon: HandCoins,
                        selectedTitle: 'Advance Verification',
                        emptyTitle: 'No Advance Selected',
                        emptyMessage: 'Select an advance from the list to review and verify.',
                        headerGradient: 'from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20',
                        maxHeight: 'calc(100vh - 200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyLabourAdvance;
